import React, { useCallback, useEffect } from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';

import useComponentDidMount from 'Hooks/useComponentDidMount';
import { addQuery } from 'Helpers/Query';
import modalConsts from 'Consts/ModalNames';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { openModal } from 'Helpers/ModalHelper';
import { buildModalQuery } from 'Consts/ModalQueries';

const OnboardingPlannerViewSectionContext = React.createContext(undefined);

// Actions
export const ONBOARDING_PLANNER_VIEW_SECTION_SET_RESOURCE = 'onboarding-planner-view-set-resource';
export const ONBOARDING_PLANNER_VIEW_SECTION_SET_NOTE_COLLECTION = 'onboarding-planner-view-set-note-collection';
export const ONBOARDING_PLANNER_VIEW_SECTION_SET_NOTE_LOADING = 'onboarding-planner-view-set-note-loading';
export const ONBOARDING_PLANNER_VIEW_SECTION_SET_NOTE_LOADING_ERROR = 'onboarding-planner-view-set-note-loading-error';
export const ONBOARDING_PLANNER_VIEW_SECTION_REMOVE_NOTE = 'onboarding-planner-view-remove-note';

// Initialize State
const init = () => {
  return {
    viewSectionResource: null,
    noteCollection: null,
    loading: false,
    loadingError: null,
    noteLoading: false,
    noteLoadingError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case ONBOARDING_PLANNER_VIEW_SECTION_SET_RESOURCE:
      return {
        ...state,
        viewSectionResource: action.resource,
        loading: false,
        loadingError: null,
      };
    case ONBOARDING_PLANNER_VIEW_SECTION_SET_NOTE_COLLECTION:
      return {
        ...state,
        noteCollection: action.collection,
        noteLoading: false,
        noteLoadingError: null,
      };
    case ONBOARDING_PLANNER_VIEW_SECTION_SET_NOTE_LOADING:
      return {
        ...state,
        noteCollection: null,
        noteLoading: true,
        noteLoadingError: null,
      };
    case ONBOARDING_PLANNER_VIEW_SECTION_SET_NOTE_LOADING_ERROR:
      return {
        ...state,
        noteCollection: null,
        noteLoading: false,
        noteLoadingError: action.error,
      };
    case ONBOARDING_PLANNER_VIEW_SECTION_REMOVE_NOTE: {
      const collectionCopy = state.noteCollection.copy();
      collectionCopy.resources().removeItem(action.resource.id());

      return {
        ...state,
        noteCollection: collectionCopy,
      };
    }
    default: {
      throw new Error(`Unhandled view section context action type: ${action.type}`);
    }
  }
};

const OnboardingPlannerViewSectionProvider = ({
  viewSectionResource,
  children,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);
  const { displayErrorMessage } = useSnackbarContext();

  const loadNoteCollection = useCallback(async () => {
    if (!viewSectionResource.relationship(fr.relationships.sectionNotes.name)) {
      return;
    }

    dispatch({
      type: ONBOARDING_PLANNER_VIEW_SECTION_SET_NOTE_LOADING,
    });

    try {
      const noteCollection = await viewSectionResource.relationship(fr.relationships.sectionNotes.name).link('related').get();
      dispatch({
        type: ONBOARDING_PLANNER_VIEW_SECTION_SET_NOTE_COLLECTION,
        collection: noteCollection,
      });
    } catch (err) {
      displayErrorMessage(err, err.errors);
      dispatch({
        type: ONBOARDING_PLANNER_VIEW_SECTION_SET_NOTE_LOADING_ERROR,
        error: err,
      });
    }
  }, [viewSectionResource]);


  useComponentDidMount(() => {
    dispatch({
      type: ONBOARDING_PLANNER_VIEW_SECTION_SET_RESOURCE,
      resource: viewSectionResource,
    });
    loadNoteCollection();
  });

  useEffect(() => {
    window.addEventListener('refreshNotes', loadNoteCollection);

    return () => {
      window.removeEventListener('refreshNotes', loadNoteCollection);
    };
  }, [loadNoteCollection]);

  const onAddTask = useCallback(async () => {
    try {
      addQuery({ modal: modalConsts.simpleActionItemLinkModal }, { simpleActionItemLink: viewSectionResource.relationship('addTask').link('related').url() });
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  }, [viewSectionResource]);

  const onDeleteNote = useCallback(async (noteResource) => {
    try {
      await noteResource.delete();
      dispatch({
        type: ONBOARDING_PLANNER_VIEW_SECTION_REMOVE_NOTE,
        resource: noteResource,
      });
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  }, []);

  const onEditNote = useCallback(async (noteResource) => {
    try {
      openModal(
        buildModalQuery(modalConsts.advisorNoteLinkModal, {
          link: noteResource.link('self').url(),
        }),
      );
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  }, []);

  const onAddNote = useCallback(async () => {
    try {
      openModal(
        buildModalQuery(modalConsts.advisorNoteLinkModal, {
          link: viewSectionResource.relationship('addNote').link('related').url(),
        }),
      );
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  }, [viewSectionResource]);

  const memoizedValue = React.useMemo(() => {
    return {
      viewSectionResource: state.viewSectionResource,
      noteCollection: state.noteCollection,
      onAddTask: viewSectionResource?.relationship(fr.relationships.addTask.name) ? onAddTask : null,
      onAddNote: viewSectionResource?.relationship(fr.relationships.addNote.name) ? onAddNote : null,
      loading: state.loading,
      onDeleteNote,
      onEditNote,
      loadingError: state.loadingError,
      noteLoading: state.noteLoading,
      noteLoadingError: state.noteLoadingError,
    };
  }, [
    state,
    onAddTask,
    onAddNote,
    onDeleteNote,
    onEditNote,
  ]);

  return (
    <OnboardingPlannerViewSectionContext.Provider value={memoizedValue}>
      {children}
    </OnboardingPlannerViewSectionContext.Provider>
  );
};


const useOnboardingPlannerViewSectionContext = () => {
  const context = React.useContext(OnboardingPlannerViewSectionContext);

  if (context === undefined) {
    throw new Error('useOnboardingPlannerViewSectionContext must be used within a OnboardingPlannerViewSectionProvider');
  }

  return context;
};


OnboardingPlannerViewSectionProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  viewSectionResource: PropTypes.object.isRequired,
};


export {
  OnboardingPlannerViewSectionProvider,
  useOnboardingPlannerViewSectionContext,

};
